import { css } from '@emotion/react';
import {
  useLayoutEffect, useRef,
} from 'react';
import type { Uuid } from '~/_shared/utils/createUuid';
import { ChartItemComponent } from './chartItem/chartItem.component';
import type { ChartItem } from './chartItem/chartItem.types';

const containerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
});

export type ChartsGalleryComponentProps = Readonly<{
  className?: string;
  chartItems: ReadonlyArray<ChartItem>;
  scrollToChartItemId?: Uuid;
  onScrolledToChartItem?: () => void;
}>;

export const ChartsGalleryComponent = ({
  className,
  chartItems,
  scrollToChartItemId,
  onScrolledToChartItem,
}: ChartsGalleryComponentProps) => {
  const onScrolledToChartItemRef = useRef(onScrolledToChartItem);
  const chartContainersRefs = useRef<Record<Uuid, HTMLDivElement | null>>({});

  // Effect to handle scrolling when scrollToChartItemId changes
  useLayoutEffect(() => {
    if (scrollToChartItemId) {
      const chartElement = chartContainersRefs.current[scrollToChartItemId];

      if (chartElement) {
        onScrolledToChartItemRef.current?.();

        setTimeout(() => {
          const chartElementRefAfterTimeout = chartContainersRefs.current[scrollToChartItemId];
          // Scroll the element into view with smooth behavior
          chartElementRefAfterTimeout?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 1000);
      }
    }
  }, [scrollToChartItemId, onScrolledToChartItemRef]);

  return (
    <div className={className} css={containerStyle}>
      {chartItems.map((chartItem) => (
        <ChartItemComponent
          ref={el => {
            chartContainersRefs.current[chartItem.id] = el;
          }}
          key={chartItem.id}
          {...chartItem}
        />
      ))}
    </div>
  );
};
